import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faSpinner, faBell, faShoppingCart } from '@fortawesome/free-solid-svg-icons';

/* ── Magnetic CTA button ─────────────────────────────────────────── */
const MagneticButton = ({ children, className, onClick, disabled }) => {
  const ref = useRef(null);
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const x = useSpring(rawX, { stiffness: 280, damping: 28 });
  const y = useSpring(rawY, { stiffness: 280, damping: 28 });

  const handleMouseMove = (e) => {
    if (disabled || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    rawX.set((e.clientX - (rect.left + rect.width / 2)) * 0.28);
    rawY.set((e.clientY - (rect.top + rect.height / 2)) * 0.28);
  };
  const handleMouseLeave = () => {
    rawX.set(0);
    rawY.set(0);
  };

  return (
    <motion.button
      ref={ref}
      className={className}
      onClick={onClick}
      disabled={disabled}
      style={{ x, y }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      whileTap={!disabled ? { scale: 0.95 } : {}}
    >
      {children}
    </motion.button>
  );
};

/* ── Animation variants ─────────────────────────────────────────── */
const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.48, ease: [0.4, 0, 0.2, 1] },
  },
};

/* ── ProductActions ─────────────────────────────────────────────── */
const ProductActions = ({
  product,
  selectedVariant,
  onVariantChange,
  isFavorite,
  togglingFavorite,
  onToggleFavorite,
  addingToCart,
  onAddToCart,
  hasPriceAlert,
  settingPriceAlert,
  onTogglePriceAlert,
  isSeller,
  isReseller,
  formatCurrency,
  getCurrentPrice,
  isFlashDealActive,
  getCurrentStock,
  getAverageRating,
}) => {
  const stock = getCurrentStock();
  const price = getCurrentPrice();
  const rating = getAverageRating();
  const outOfStock = stock === 0;

  const restrictedConditions = ['nigerian_used', 'nigeria_used', 'for_sale', 'want_to_buy'];
  const isRestrictedForReseller = isReseller && restrictedConditions.includes(product.condition);

  return (
    <motion.div
      className="pd-actions"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Title */}
      <motion.h1 className="pd-actions__title" variants={itemVariants}>
        {product.title || product.name}
      </motion.h1>

      {/* Rating row */}
      {rating > 0 && (
        <motion.div className="pd-actions__rating" variants={itemVariants}>
          <span className="pd-actions__stars">
            {[1, 2, 3, 4, 5].map((s) => (
              <span key={s} style={{ opacity: s <= Math.round(rating) ? 1 : 0.22 }}>★</span>
            ))}
          </span>
          <span className="pd-actions__rating-text">
            {rating.toFixed(1)}&nbsp;·&nbsp;{product.total_reviews || 0} reviews
          </span>
        </motion.div>
      )}

      {/* Price */}
      <motion.div className="pd-actions__price-wrap" variants={itemVariants}>
        {isSeller && product.price && (product.seller_price || product.sellerPrice) ? (
          <div className="pd-actions__dual-price">
            <div>
              <span className="pd-actions__price-label">Retail</span>
              <span className="pd-actions__price">{formatCurrency(product.price)}</span>
            </div>
            <div>
              <span className="pd-actions__price-label pd-actions__price-label--wholesale">Wholesale</span>
              <span className="pd-actions__price pd-actions__price--accent">
                {formatCurrency(product.seller_price || product.sellerPrice)}
              </span>
            </div>
          </div>
        ) : isFlashDealActive ? (
          <div className="pd-actions__dual-price">
            <span className="pd-actions__price pd-actions__price--main" style={{ color: '#EA580C' }}>
              {formatCurrency(price)}
            </span>
            <span className="pd-actions__price" style={{ textDecoration: 'line-through', opacity: 0.55, fontWeight: 400 }}>
              {formatCurrency(product.price)}
            </span>
            <span style={{ background: '#EA580C', color: '#fff', fontWeight: 700, fontSize: '0.72rem', padding: '0.2rem 0.55rem', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
              <i className="fas fa-bolt" /> FLASH DEAL
            </span>
          </div>
        ) : (
          <span className="pd-actions__price pd-actions__price--main">
            {formatCurrency(price)}
          </span>
        )}
      </motion.div>

      {/* Tags row (condition, brand, stock) */}
      <motion.div className="pd-actions__tags" variants={itemVariants}>
        {product.condition && (
          <span className="pd-actions__tag">
            {product.condition.replace('_', ' ').toUpperCase()}
          </span>
        )}
        {product.brand && (
          <span className="pd-actions__tag">{product.brand}</span>
        )}
        <span className={`pd-actions__stock-pill${outOfStock ? ' is-out' : ' is-in'}`}>
          {outOfStock ? '⚠ Out of Stock' : `✓ In Stock (${stock})`}
        </span>
      </motion.div>

      {/* Description excerpt */}
      {product.description && (
        <motion.p className="pd-actions__desc" variants={itemVariants}>
          {product.description.substring(0, 200)}
          {product.description.length > 200 ? '…' : ''}
        </motion.p>
      )}

      {/* Variants */}
      {product.variants && product.variants.length > 0 && (
        <motion.div className="pd-actions__variants" variants={itemVariants}>
          <p className="pd-actions__variants-label">Select Variant</p>
          <div className="pd-actions__variants-row">
            {product.variants.map((variant, i) => (
              <button
                key={i}
                className={`pd-actions__variant-btn${selectedVariant === variant ? ' is-active' : ''}`}
                onClick={() => onVariantChange(variant)}
              >
                <span>{variant.name || variant.storage || `Option ${i + 1}`}</span>
                {variant.price && (
                  <span className="pd-actions__variant-price">
                    {formatCurrency(variant.price)}
                  </span>
                )}
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* CTA Row */}
      <motion.div className="pd-actions__cta-row" variants={itemVariants}>
        {!isRestrictedForReseller ? (
          <MagneticButton
            className={`pd-actions__add-btn${outOfStock ? ' is-disabled' : ''}`}
            onClick={onAddToCart}
            disabled={addingToCart || outOfStock}
          >
            {addingToCart ? (
              <><FontAwesomeIcon icon={faSpinner} spin />&nbsp;&nbsp;Adding…</>
            ) : (
              <><FontAwesomeIcon icon={faShoppingCart} />&nbsp;&nbsp;Add to Cart</>
            )}
          </MagneticButton>
        ) : (
          <div className="pd-actions__restricted-msg">
            <span className="pd-actions__tag pd-actions__tag--restricted">Reseller Purchase Restricted</span>
          </div>
        )}

        <motion.button
          className={`pd-actions__icon-btn${isFavorite ? ' is-heart' : ''}`}
          onClick={onToggleFavorite}
          disabled={togglingFavorite}
          whileTap={{ scale: 0.86 }}
          title={isFavorite ? 'Remove from Wishlist' : 'Add to Wishlist'}
        >
          <FontAwesomeIcon icon={faHeart} />
        </motion.button>

        <motion.button
          className={`pd-actions__icon-btn${hasPriceAlert ? ' is-bell' : ''}`}
          onClick={onTogglePriceAlert}
          disabled={settingPriceAlert}
          whileTap={{ scale: 0.86 }}
          title={hasPriceAlert ? 'Remove price alert' : 'Notify me if price drops'}
        >
          <FontAwesomeIcon icon={faBell} />
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default ProductActions;
